'use client';

import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

/** Remove all Supabase auth keys from localStorage */
export function clearAuthStorage() {
  if (typeof localStorage === 'undefined') return;
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('sb-') || k.startsWith('sb_'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
}

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((c) => {
          const idx = c.trim().indexOf('=');
          const name = idx !== -1 ? c.trim().slice(0, idx) : c.trim();
          const rest = idx !== -1 ? c.trim().slice(idx + 1) : '';
          return { name: name.trim(), value: decodeURIComponent(rest) };
        })
        .filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
};

const setCookie = (name: string, value: string, options?: any) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
};

/**
 * No-op lock: bypasses navigator.locks entirely.
 * This prevents the "Lock broken by another request with the 'steal' option"
 * AbortError that occurs in React Strict Mode when multiple getSession() calls
 * compete for the same Web Lock.
 */
const noopLock: (name: string, acquireOptions: any, fn: () => Promise<any>) => Promise<any> =
  (_name, _options, fn) => fn();

function buildClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        lock: noopLock,
      },
      cookies: {
        getAll: () => (canUseCookies() ? fromCookies() : fromStorage()),
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) =>
              value
                ? setCookie(name, value, options)
                : (document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`)
            );
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                value
                  ? localStorage.setItem(`${PFX}${name}`, value)
                  : localStorage.removeItem(`${PFX}${name}`);
              } catch {}
              if (value) setCookie(name, value, options);
            });
          }
        },
      },
    }
  );
}

// Singleton instance — prevents multiple clients competing for the auth-token lock
let clientInstance: ReturnType<typeof createBrowserClient> | null = null;

export function createClient() {
  if (!clientInstance) {
    clientInstance = buildClient();
  }
  return clientInstance;
}

/** Call this to force a fresh client (e.g. after auth errors) */
export function resetClient() {
  clientInstance = null;
}
